// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { AMLineChartComponent } from './components/charts/am-line-chart/am-line-chart.component';
import { AMBarChartComponent } from './components/charts/am-bar-chart/am-bar-chart.component';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink, RouterOutlet } from '@angular/router';
import { importEcharts } from '@lfx/shared/echarts';
import { NgbDropdownConfig, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { HighchartsChartModule } from 'highcharts-angular';
import { NgxEchartsModule } from 'ngx-echarts';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgxSpinnerModule } from 'ngx-spinner';

import { AlertComponent } from './components/alert/alert.component';
import { AutoCompleteSearchComponent } from './components/autocomplete-search/autocomplete-search.component';
import { BadgeComponent } from './components/badge/badge.component';
import { CascadingRowTableComponent } from './components/cascading-row-table/cascading-row-table.component';
import { BarChartComponent } from './components/charts/bar-chart/bar-chart.component';
import { DoughnutChartComponent } from './components/charts/doughnut-chart/doughnut-chart.component';
import { GaugeChartComponent } from './components/charts/gauge-chart/gauge-chart.component';
import { LineChartComponent } from './components/charts/line-chart/line-chart.component';
import { NumberWidgetComponent } from './components/charts/number-widget/number-widget.component';
import { PieChartComponent } from './components/charts/pie-chart/pie-chart.component';
import { StakedBarChartComponent } from './components/charts/staked-bar-chart/staked-bar-chart.component';
import { TwoSeriesBarChartComponent } from './components/charts/two-series-bar-chart/two-series-bar-chart.component';
import { WaterfallChartComponent } from './components/charts/waterfall-chart/waterfall-chart.component';
import { WorldMapChartComponent } from './components/charts/world-map-chart/world-map-chart.component';
import { CompareByDropdownComponent } from './components/compare-by-dropdown/compare-by-dropdown.component';
import { CoverMiniCardComponent } from './components/cover-mini-card/cover-mini-card.component';
import { DateRangeDropdownComponent } from './components/date-range-dropdown/date-range-dropdown.component';
import { FilterDropdownComponent } from './components/filter-dropdown/filter-dropdown.component';
import { IconStatusDialogComponent } from './components/icon-status-dialog/icon-status-dialog.component';
import { InsightsCardComponent } from './components/insights-card/insights-card.component';
import { MiniTagsComponent } from './components/mini-tags/mini-tags.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { ConfirmationModalComponent } from './components/popups/confirmation/confirmation.component';
import { QuickNavComponent } from './components/quick-nav/quick-nav.component';
import { SearchBoxComponent } from './components/search-box/search-box.component';
import { TopBtnComponent } from './components/top-btn/top-btn.component';
import { UsageBarComponent } from './components/usage-bar/usage-bar.component';
import { ClickOutsideDirective } from './directives/click-outside';
import { DraggableDirective } from './directives/draggable.directive';
import { LetDirective } from './directives/let.directive';
import { LfxPermissionsAllDirective } from './directives/lfx-permissions-all.directive';
import { MovableDirective } from './directives/movable.directive';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { DomainFormatPipe } from './pipes/domain-format.pipe';
import { EllipsisPipe } from './pipes/ellipsis.pipe';
import { FilterArrayOfObjectsPipe } from './pipes/filter-array-of-objects.pipe';
import { HighlightPipe } from './pipes/highlight.pipe';
import { SanitizeHtml } from './pipes/sanitize.pipe';
import { ShortNumberPipe } from './pipes/short-number.pipe';
import { SortArrayOfObjectsPipe } from './pipes/sort-array-of-objects.pipe';
import { TextShortenPipe } from './pipes/textshorten.pipe';
import { UtcDatePipe } from './pipes/utc-date.pipe';
import { LineAreaChartComponent } from './components/charts/line-area-chart/line-area-chart.component';
import { TwoLayerFunnelChartComponent } from './components/charts/two-layer-funnel-chart/two-layer-funnel-chart.component';
import { OutlineBtnComponent } from './components/outline-btn/outline-btn.component';
import { ImageErrorFallbackDirective } from './directives/image-error-fallback.directive';
import { NgbPaginationWrapperComponent } from './components/ngb-pagination-wrapper/ngb-pagination-wrapper.component';
import { ProjectCardComponent } from './components/project-card/project-card.component';
import { EmployeeInsightsDetailsDialogComponent } from './components/employee-insights-details-dialog/employee-insights-details-dialog.component';
import { EventsDetailsSpeakersTableComponent } from './components/events-details-speakers-table/events-details-speakers-table.component';
import { ScreenshotTakerComponent } from './components/screenshot-taker/screenshot-taker.component';
import { FirstKeyPipe } from './pipes/first-key.pipe';
import { DashPipe } from './pipes/dash.pipe';
import { AvatarComponent } from './components/avatar/avatar.component';
import { LineEchartsComponent } from './components/charts/line-echarts/line-echarts.component';
import { DragDropDocumentsDirective } from './directives/drag-drop-documents.directive';
import { PiiHelpIconComponent } from './components/pii-help-icon/pii-help-icon.component';
import { NewTableComponent } from './components/new-table/new-table.component';
import { UploadLogoDialogComponent } from './components/upload-logo-dialog/upload-logo-dialog.component';
import { BenefitsUsedStatusComponent } from './components/benefits-used-status/benefits-used-status.component';
import { ErrorAlertComponent } from './components/error-alert/error-alert.component';
import { PercentageBarComponent } from './components/percentage-bar/percentage-bar.component';
import { RadarChartComponent } from './components/charts/radar-chart/radar-chart.component';
import { AMCircleProgressComponent } from './components/charts/am-circle-progress/am-circle-progress.component';
import { AMMapChartComponent } from './components/charts/am-map-chart/am-map-chart.component';
import { LfBoxComponent } from './components/lf-box/lf-box.component';
import { LfMetricComponent } from './components/lf-metric/lf-metric.component';
import { DashboardAccessPipe } from './pipes/dashboard-access.pipe';
import { DisclaimerComponent } from './components/disclaimer/disclaimer.component';
import { OrgNestingPopoverComponent } from './components/org-nesting-popover/org-nesting-popover.component';
import { PercentagePipe } from './pipes/percentage.pipe';
import { SbomNoFilesStateComponent } from './components/sbom-no-files-state/sbom-no-files-state.component';
import { UrlFormatPipe } from './pipes/url-format.pipe';
import { DashboardSoftwareValuationComponent } from './components/dashboard-software-valuation/dashboard-software-valuation.component';
import { FirstNPipe } from './pipes/first-n-value-of-array.pipe';
import { ExternalLinkComponent } from './components/external-link/external-link.component';
import { StintComponent } from './components/stint/stint.component';
import { InfoOffcanvasComponent } from './components/info-offcanvas/info-offcanvas.component';
import { ProjectsDropdownComponent } from './components/projects-dropdown/projects-dropdown.component';
import { OverviewTabComponent } from './components/overview-tab/overview-tab.component';
import { FilterChipsComponent } from './components/filter-chips/filter-chips.component';
import { UserProfileCellComponent } from './components/user-profile-cell/user-profile-cell.component';
import { SectionFooterComponent } from './components/section-footer/section-footer.component';
import { EventsProjectsDropdownComponent } from './components/events-projects-dropdown/events-projects-dropdown.component';
import { EventRegistrantsOffcanvasComponent } from './components/event-registrants-offcanvas/event-registrants-offcanvas.component';
import { EventSpeakersOffcanvasComponent } from './components/event-speakers-offcanvas/event-speakers-offcanvas.component';
import { TrainingNCertificateDetailsComponent } from './components/training-n-certificate-details/training-n-certificate-details.component';
import { MailingListSubscribersOffcanvasComponent } from './components/mailing-list-subscribers-offcanvas/mailing-list-subscribers-offcanvas.component';
import { MeetingsAttendeesOffcanvasComponent } from './components/meetings-attendees-offcanvas/meetings-attendees-offcanvas.component';
import { ToggleComponent } from './components/toggle/toggle.component';
import { MembershipProjectsOffcanvasComponent } from './components/membership-projects-offcanvas/membership-projects-offcanvas.component';
import { StatusDotComponent } from './components/status-dot/status-dot.component';
import { RepositoryDetailsOffcanvasComponent } from './components/repository-details-offcanvas/repository-details-offcanvas.component';
import { ConfirmationModalWithoutHeadersComponent } from './components/popups/confirmation-without-headers/confirmation-without-headers.component';
import { CapitalizePipe } from './pipes/capitalize.pipe';
import { FullAddress } from './pipes/full-address.pipe';

@NgModule({
  declarations: [
    AlertComponent,
    AutoCompleteSearchComponent,
    BadgeComponent,
    CascadingRowTableComponent,
    BarChartComponent,
    DoughnutChartComponent,
    GaugeChartComponent,
    LineChartComponent,
    NumberWidgetComponent,
    PieChartComponent,
    StakedBarChartComponent,
    TwoSeriesBarChartComponent,
    WaterfallChartComponent,
    WorldMapChartComponent,
    CompareByDropdownComponent,
    CoverMiniCardComponent,
    DateRangeDropdownComponent,
    FilterDropdownComponent,
    IconStatusDialogComponent,
    InsightsCardComponent,
    MiniTagsComponent,
    PaginationComponent,
    PaginatorComponent,
    ConfirmationModalWithoutHeadersComponent,
    ConfirmationModalComponent,
    QuickNavComponent,
    SearchBoxComponent,
    TopBtnComponent,
    UsageBarComponent,
    ClickOutsideDirective,
    DraggableDirective,
    LetDirective,
    LfxPermissionsAllDirective,
    MovableDirective,
    DateFormatPipe,
    DomainFormatPipe,
    EllipsisPipe,
    FilterArrayOfObjectsPipe,
    HighlightPipe,
    SanitizeHtml,
    ShortNumberPipe,
    SortArrayOfObjectsPipe,
    TextShortenPipe,
    UtcDatePipe,
    LineAreaChartComponent,
    TwoLayerFunnelChartComponent,
    OutlineBtnComponent,
    ImageErrorFallbackDirective,
    NgbPaginationWrapperComponent,
    ProjectCardComponent,
    EmployeeInsightsDetailsDialogComponent,
    EventsDetailsSpeakersTableComponent,
    ScreenshotTakerComponent,
    DashPipe,
    FirstKeyPipe,
    AvatarComponent,
    LineEchartsComponent,
    DragDropDocumentsDirective,
    PiiHelpIconComponent,
    NewTableComponent,
    UploadLogoDialogComponent,
    BenefitsUsedStatusComponent,
    ErrorAlertComponent,
    AMBarChartComponent,
    AMLineChartComponent,
    AMCircleProgressComponent,
    AMMapChartComponent,
    PercentageBarComponent,
    RadarChartComponent,
    LfBoxComponent,
    LfMetricComponent,
    DashboardAccessPipe,
    DisclaimerComponent,
    OrgNestingPopoverComponent,
    PercentagePipe,
    SbomNoFilesStateComponent,
    UrlFormatPipe,
    DashboardSoftwareValuationComponent,
    FirstNPipe,
    ExternalLinkComponent,
    StintComponent,
    InfoOffcanvasComponent,
    ProjectsDropdownComponent,
    OverviewTabComponent,
    FilterChipsComponent,
    UserProfileCellComponent,
    SectionFooterComponent,
    EventsProjectsDropdownComponent,
    EventRegistrantsOffcanvasComponent,
    EventSpeakersOffcanvasComponent,
    TrainingNCertificateDetailsComponent,
    MailingListSubscribersOffcanvasComponent,
    MeetingsAttendeesOffcanvasComponent,
    ToggleComponent,
    MembershipProjectsOffcanvasComponent,
    StatusDotComponent,
    RepositoryDetailsOffcanvasComponent,
    CapitalizePipe,
    FullAddress,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    RouterLink,
    RouterOutlet,
    NgxPermissionsModule.forChild(),
    NgSelectModule,
    NgxEchartsModule.forRoot({
      echarts: importEcharts,
    }),
    HighchartsChartModule,
    NgxSpinnerModule,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    NgbModule,
    AlertComponent,
    AutoCompleteSearchComponent,
    BadgeComponent,
    MiniTagsComponent,
    QuickNavComponent,
    TopBtnComponent,
    ClickOutsideDirective,
    FullAddress,
    ShortNumberPipe,
    SortArrayOfObjectsPipe,
    NgxPermissionsModule,
    BarChartComponent,
    LineChartComponent,
    PieChartComponent,
    DoughnutChartComponent,
    GaugeChartComponent,
    NumberWidgetComponent,
    DateFormatPipe,
    DomainFormatPipe,
    LfxPermissionsAllDirective,
    UtcDatePipe,
    PaginatorComponent,
    CascadingRowTableComponent,
    HighlightPipe,
    AutoCompleteSearchComponent,
    DateRangeDropdownComponent,
    EllipsisPipe,
    InsightsCardComponent,
    IconStatusDialogComponent,
    FilterDropdownComponent,
    FilterArrayOfObjectsPipe,
    LetDirective,
    TwoSeriesBarChartComponent,
    WaterfallChartComponent,
    CompareByDropdownComponent,
    HighchartsChartModule,
    WorldMapChartComponent,
    StakedBarChartComponent,
    UsageBarComponent,
    SearchBoxComponent,
    NgxEchartsModule,
    LineAreaChartComponent,
    TwoLayerFunnelChartComponent,
    OutlineBtnComponent,
    ImageErrorFallbackDirective,
    NgbPaginationWrapperComponent,
    ProjectCardComponent,
    EmployeeInsightsDetailsDialogComponent,
    EventsDetailsSpeakersTableComponent,
    ScreenshotTakerComponent,
    DashPipe,
    FirstKeyPipe,
    AvatarComponent,
    LineEchartsComponent,
    DragDropDocumentsDirective,
    PiiHelpIconComponent,
    NewTableComponent,
    UploadLogoDialogComponent,
    BenefitsUsedStatusComponent,
    ErrorAlertComponent,
    AMBarChartComponent,
    AMLineChartComponent,
    AMCircleProgressComponent,
    AMMapChartComponent,
    PercentageBarComponent,
    RadarChartComponent,
    LfBoxComponent,
    LfMetricComponent,
    DashboardAccessPipe,
    DisclaimerComponent,
    OrgNestingPopoverComponent,
    PercentagePipe,
    SbomNoFilesStateComponent,
    UrlFormatPipe,
    DashboardSoftwareValuationComponent,
    FirstNPipe,
    ExternalLinkComponent,
    StintComponent,
    ProjectsDropdownComponent,
    OverviewTabComponent,
    FilterChipsComponent,
    UserProfileCellComponent,
    SectionFooterComponent,
    EventsProjectsDropdownComponent,
    ToggleComponent,
    MembershipProjectsOffcanvasComponent,
    StatusDotComponent,
    RepositoryDetailsOffcanvasComponent,
    CapitalizePipe,
    NgxSpinnerModule,
  ],
  providers: [NgbDropdownConfig, SortArrayOfObjectsPipe, ShortNumberPipe],
})
export class SharedModule {}
